// crossSystem.ts — PIERWSZA ANALIZA ŁĄCZĄCA SPRZEDAŻ Z DOSTAWAMI.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  CO TO ROBI, A CZEGO NIE ROBI
//
//  Zestawia dwie NIEZALEŻNIE policzone listy — sprzedaż per szkoła (CRM) i punktualność per
//  szkoła (Drive) — po wspólnym kluczu `tuszama_id`, i pokazuje, gdzie oba wskaźniki pogorszyły
//  się w TYM SAMYM oknie czasowym.
//
//  To jest WSPÓŁWYSTĘPOWANIE. Nie jest to dowód, że opóźnienia obniżyły sprzedaż. Może być
//  odwrotnie, może być wspólna przyczyna, może być zbieg okoliczności. Dlatego wynik nazywa się
//  „zbieżność”, ma pole `causation: 'NOT_ESTABLISHED'`, i podaje test, który mógłby to rozstrzygnąć.
//
//  DLACZEGO PO STRONIE PRZEGLĄDARKI, A NIE NA SERWERZE (ADR-002)
//  Bo to jest scalenie dwóch OGRANICZONYCH list agregatów — kilkadziesiąt wierszy po każdej
//  stronie, złączenie po kluczu, zero zapytań do bazy. Warstwa pośrednia (BFF) nie dodałaby tu
//  niczego poza kolejnym systemem do utrzymania. WARUNEK REWIZJI: gdyby ta analiza kiedykolwiek
//  potrzebowała danych spoza już pobranych agregatów albo zaczęła iterować po dniach czy
//  pozycjach, przenosimy ją na serwer.
// ══════════════════════════════════════════════════════════════════════════════════════════════

import type { DeliverySchoolRow } from './api/drive';
import type { DataQuality } from './dataQuality';
import { worstQuality } from './dataQuality';

export const CROSS_ENGINE_VERSION = 'sales-delivery-coincidence-v0.1';

export interface CoincidenceThresholds {
  /** Minimalny spadek sprzedaży (w procentach), żeby w ogóle mówić o sygnale. */
  minSalesDropPct: number;
  /** Minimalne pogorszenie punktualności (w punktach procentowych). */
  minOnTimeDropPoints: number;
  /** Minimalna liczba dostaw w oknie — poniżej tego punktualność jest szumem. */
  minDeliveries: number;
  /** Minimalna liczba zamówień w oknie — poniżej tego sprzedaż jest szumem. */
  minOrders: number;
}

export const DEFAULT_THRESHOLDS: CoincidenceThresholds = {
  minSalesDropPct: 10,
  minOnTimeDropPoints: 10,
  minDeliveries: 10,
  minOrders: 20,
};

export interface SalesWindowRow {
  /**
   * Identyfikator szkoły w systemie głównym — klucz złączenia.
   * Typ celowo dopuszcza string: CRM zwraca go jako tekst (BIGINT w PostgreSQL trafia do JSON-a
   * jako łańcuch), a Drive jako liczbę (INTEGER). Normalizacja jest w joinKey().
   */
  school_id: number | string;
  school_name: string | null;
  city: string | null;
  sales_grosze: number;
  orders: number;
}

export interface Coincidence {
  school_id: number;
  school_name: string | null;
  city: string | null;
  sales_current_grosze: number;
  sales_previous_grosze: number;
  sales_delta_pct: number | null;
  orders_current: number;
  orders_previous: number;
  on_time_current_pct: number | null;
  on_time_previous_pct: number | null;
  on_time_delta_points: number | null;
  avg_delta_current_min: number | null;
  avg_delta_change_min: number | null;
  deliveries: number;
  data_quality: DataQuality;
  data_quality_reason: string;
}

export type JoinStatus = 'READY' | 'PARTIAL' | 'BLOCKED';

export interface JoinReport {
  status: JoinStatus;
  sales_schools: number;
  delivery_schools: number;
  matched: number;
  sales_only: number;
  delivery_only: number;
  delivery_rows_without_id: number;
  match_pct: number | null;
  reason: string;
}

/**
 * Normalizacja klucza złączenia — JEDNO miejsce, w którym godzimy dwa systemy.
 *
 * DLACZEGO TO ISTNIEJE: identyfikator szkoły jest w CRM kolumną BIGINT, a w Drive kolumną INTEGER.
 * Sterownik PostgreSQL oddaje BIGINT jako ŁAŃCUCH (bo nie mieści się bezpiecznie w liczbie JS),
 * a INTEGER jako liczbę. Porównanie 8001 === '8001' jest fałszem, więc bez tej normalizacji
 * złączenie po cichu nie dopasowuje NICZEGO — a wykres wygląda tak samo, jakby po prostu nie było
 * sygnału. Ten defekt został wykryty dopiero na realnych odpowiedziach obu API, nie na atrapach.
 *
 * Zwraca null dla wartości, których nie da się uczciwie porównać — takie rekordy wypadają
 * ze złączenia zamiast trafić do niego przypadkiem.
 */
export function joinKey(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function deltaPct(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Number((((current - previous) / previous) * 100).toFixed(1));
}

/**
 * Ocena wiarygodności ZŁĄCZENIA, osobno od jakości każdej ze stron.
 * Szkoła obecna tylko po jednej stronie nie jest błędem — może po prostu nie mieć dostaw
 * albo nie mieć sprzedaży. Ale gdy dopasowanie jest niskie, żaden wniosek nie jest bezpieczny.
 */
export function assessJoin(
  sales: readonly SalesWindowRow[],
  deliveries: readonly DeliverySchoolRow[],
): JoinReport {
  const salesIds = new Set(sales.map((s) => joinKey(s.school_id)).filter((id): id is number => id !== null));
  const deliveryIds = new Set(deliveries.map((d) => joinKey(d.tuszama_id)).filter((id): id is number => id !== null));
  const matched = [...salesIds].filter((id) => deliveryIds.has(id)).length;
  const rowsWithoutId = deliveries.reduce((a, d) => a + (d.without_tuszama_id || 0), 0);
  const denominator = Math.max(salesIds.size, deliveryIds.size);
  const matchPct = denominator > 0 ? Number(((matched / denominator) * 100).toFixed(1)) : null;

  let status: JoinStatus = 'BLOCKED';
  let reason: string;
  if (deliveryIds.size === 0) {
    reason = 'Żadna dostawa nie ma identyfikatora szkoły z systemu głównego — nie ma po czym łączyć.';
  } else if (matched === 0) {
    reason = 'Zbiory identyfikatorów nie mają części wspólnej — sprzedaż i dostawy dotyczą rozłącznych szkół.';
  } else if (matchPct !== null && matchPct < 60) {
    status = 'PARTIAL';
    reason = `Dopasowano ${matched} z ${denominator} szkół (${matchPct}%). Wnioski dotyczą wyłącznie dopasowanej części.`;
  } else {
    status = 'READY';
    reason = `Dopasowano ${matched} z ${denominator} szkół (${matchPct}%).`;
  }

  return {
    status,
    sales_schools: salesIds.size,
    delivery_schools: deliveryIds.size,
    matched,
    sales_only: [...salesIds].filter((id) => !deliveryIds.has(id)).length,
    delivery_only: [...deliveryIds].filter((id) => !salesIds.has(id)).length,
    delivery_rows_without_id: rowsWithoutId,
    match_pct: matchPct,
    reason,
  };
}

/**
 * Ile szkół i DLACZEGO nie weszło do analizy zbieżności.
 *
 * ┌─ PO CO TO ISTNIEJE — TO JEST SEDNO, NIE STATYSTYKA ─────────────────────────────────────────┐
 * │ Silnik odsiewa szkoły w dwóch zupełnie różnych momentach i z dwóch różnych powodów:         │
 * │                                                                                             │
 * │   ODRZUCONA PRZED ANALIZĄ — brak porównania (nie ma poprzedniego okna, nie ma dopasowania   │
 * │                             po stronie dostaw, albo delta punktualności jest nieznana),     │
 * │   PRZEANALIZOWANA         — dane były komplet, progi po prostu nie zostały przekroczone.    │
 * │                                                                                             │
 * │ Pusta lista wygląda tak samo w obu przypadkach, a znaczy coś przeciwnego. Bez tego licznika │
 * │ ekran mówił „Silnik przeanalizował 15 szkół i żadna nie przekroczyła progów istotności.     │
 * │ To jest wynik, nie brak danych." — w sytuacji, w której NIE PRZEANALIZOWAŁ ANI JEDNEJ, bo   │
 * │ `on_time_delta_points` było null dla wszystkich i wypadały na pierwszym warunku.            │
 * │                                                                                             │
 * │ To jest dokładnie ta pomyłka, przed którą broni cały ten produkt: „nie wystąpiło" podane    │
 * │ jako wynik tam, gdzie prawdą jest „nie wiadomo".                                            │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 */
export interface CoincidencePopulation {
  /** Szkoły ze sprzedażą w oknie bieżącym — punkt wyjścia. */
  candidates: number;
  /** Faktycznie porównane: komplet danych po obu stronach. */
  analysed: number;
  /** Odrzucone przed porównaniem, z rozbiciem na powód. */
  skipped: number;
  skipped_no_school_id: number;
  skipped_no_delivery_match: number;
  skipped_no_previous_sales: number;
  skipped_no_delta: number;
}

export interface CoincidenceResult {
  coincidences: Coincidence[];
  population: CoincidencePopulation;
}

/**
 * Znajduje szkoły, w których w tym samym oknie spadła SPRZEDAŻ i pogorszyła się PUNKTUALNOŚĆ.
 * Zwraca posortowane od najmocniejszego sygnału. Pusta lista jest poprawnym wynikiem —
 * ALE tylko wtedy, gdy `population.analysed > 0`. Patrz `CoincidencePopulation`.
 */
export function findCoincidences(
  salesCurrent: readonly SalesWindowRow[],
  salesPrevious: readonly SalesWindowRow[],
  deliveries: readonly DeliverySchoolRow[],
  thresholds: CoincidenceThresholds = DEFAULT_THRESHOLDS,
): CoincidenceResult {
  const prevById = new Map(
    salesPrevious.map((s) => [joinKey(s.school_id), s] as const).filter(([id]) => id !== null),
  );
  const deliveryById = new Map(
    deliveries.map((d) => [joinKey(d.tuszama_id), d] as const).filter(([id]) => id !== null),
  );

  const out: Coincidence[] = [];
  // Każde `continue` niżej ma swój licznik. Odrzucenie bez śladu jest dokładnie tym, co
  // pozwoliło ekranowi twierdzić, że przeanalizował szkoły, których nawet nie dotknął.
  const populacja: CoincidencePopulation = {
    candidates: salesCurrent.length,
    analysed: 0,
    skipped: 0,
    skipped_no_school_id: 0,
    skipped_no_delivery_match: 0,
    skipped_no_previous_sales: 0,
    skipped_no_delta: 0,
  };

  for (const current of salesCurrent) {
    const key = joinKey(current.school_id);
    if (key === null) { populacja.skipped += 1; populacja.skipped_no_school_id += 1; continue; }
    const delivery = deliveryById.get(key);
    if (!delivery) { populacja.skipped += 1; populacja.skipped_no_delivery_match += 1; continue; }
    const previous = prevById.get(key);
    if (!previous) { populacja.skipped += 1; populacja.skipped_no_previous_sales += 1; continue; }

    const salesDelta = deltaPct(current.sales_grosze, previous.sales_grosze);
    const onTimeDelta = delivery.on_time_delta_points;
    if (salesDelta === null || onTimeDelta === null) {
      populacja.skipped += 1; populacja.skipped_no_delta += 1; continue;
    }

    // Od tego miejsca szkoła JEST porównana — dalsze odrzucenia to już wynik analizy,
    // a nie jej brak.
    populacja.analysed += 1;

    // Oba wskaźniki muszą pogorszyć się istotnie. Sam spadek sprzedaży to nie jest sygnał
    // cross-system, a samo pogorszenie dostaw to zwykły problem operacyjny.
    if (salesDelta > -thresholds.minSalesDropPct) continue;
    if (onTimeDelta > -thresholds.minOnTimeDropPoints) continue;

    const qualities: DataQuality[] = [delivery.data_quality as DataQuality];
    let reason = delivery.data_quality_reason;
    if (delivery.current.deliveries < thresholds.minDeliveries) {
      qualities.push('LOW_SAMPLE');
      reason = `Tylko ${delivery.current.deliveries} dostaw w oknie.`;
    }
    if (current.orders < thresholds.minOrders) {
      qualities.push('LOW_SAMPLE');
      reason = `Tylko ${current.orders} zamówień w oknie.`;
    }
    if (delivery.without_tuszama_id > 0) {
      qualities.push('UNRELIABLE_JOIN');
      reason = `${delivery.without_tuszama_id} dostaw bez identyfikatora szkoły — część danych nie wchodzi do złączenia.`;
    }

    out.push({
      school_id: key,
      school_name: current.school_name ?? delivery.school_name,
      city: current.city ?? delivery.city,
      sales_current_grosze: current.sales_grosze,
      sales_previous_grosze: previous.sales_grosze,
      sales_delta_pct: salesDelta,
      orders_current: current.orders,
      orders_previous: previous.orders,
      on_time_current_pct: delivery.current.on_time_pct,
      on_time_previous_pct: delivery.previous.on_time_pct,
      on_time_delta_points: onTimeDelta,
      avg_delta_current_min: delivery.current.avg_delta_min,
      avg_delta_change_min: delivery.avg_delta_change_min,
      deliveries: delivery.current.deliveries,
      data_quality: worstQuality(qualities),
      data_quality_reason: reason,
    });
  }

  // Najmocniejszy sygnał = największa suma obu pogorszeń.
  const posortowane = out.sort(
    (a, b) =>
      (a.sales_delta_pct ?? 0) + (a.on_time_delta_points ?? 0) - ((b.sales_delta_pct ?? 0) + (b.on_time_delta_points ?? 0)),
  );
  return { coincidences: posortowane, population: populacja };
}

/**
 * Czy pusty wynik wolno przedstawić jako WYNIK.
 *
 * Wolno wtedy i tylko wtedy, gdy cokolwiek zostało faktycznie porównane. Zero porównanych szkół
 * przy piętnastu kandydatkach nie jest zdaniem o sieci szkół — jest zdaniem o naszych danych.
 */
export function czyPustkaJestWynikiem(p: CoincidencePopulation): boolean {
  return p.analysed > 0;
}

/** Jednozdaniowe wyjaśnienie, dlaczego nie było czego porównać. Bez tego zostaje samo „brak". */
export function powodBrakuPorownania(p: CoincidencePopulation): string | null {
  if (p.analysed > 0) return null;
  const powody: string[] = [];
  if (p.skipped_no_delta > 0) {
    powody.push(`${p.skipped_no_delta} bez porównania punktualności między oknami`);
  }
  if (p.skipped_no_previous_sales > 0) {
    powody.push(`${p.skipped_no_previous_sales} bez sprzedaży w oknie poprzednim`);
  }
  if (p.skipped_no_delivery_match > 0) {
    powody.push(`${p.skipped_no_delivery_match} bez dopasowania do dostaw`);
  }
  if (p.skipped_no_school_id > 0) {
    powody.push(`${p.skipped_no_school_id} bez identyfikatora szkoły`);
  }
  if (powody.length === 0) return 'Brak szkół ze sprzedażą w wybranym oknie.';
  return `Żadnej z ${p.candidates} szkół nie dało się porównać: ${powody.join(', ')}.`;
}
